import { useState, useEffect, useRef, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import QRCode from "react-qr-code";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import {
  Radio, Eye, EyeOff, Copy, Check, Youtube, Facebook, Globe, MessageSquare,
  Pencil, Key, Link as LinkIcon, Upload, ImageIcon, Loader2,
  LayoutDashboard, ExternalLink, Share2, QrCode, Download, Plus, X,
  Users, PlayCircle, Heart, Sparkles, ChevronRight, CalendarClock,
  ShieldCheck, Gift, Clock,
} from "lucide-react";

import { LIVE_THEMES } from "@/lib/liveTheme";
import { initialPortalTab, type PortalTab } from "@/lib/livePortalTab";
import { keepsakeVerified } from "@/v2/lib/liveStudio";

// Gold accent used throughout the client portal
const GOLD = "#C5A572";
const GOLD_GRAD = `linear-gradient(135deg, #C5A572 0%, #E0C88A 60%, #C5A572 100%)`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LiveStream {
  id: number;
  slug: string;
  weddingId: number;
  coupleNamesOverride: string | null;
  coverImageKey: string | null;
  welcomeMessage: string | null;
  schedule: any;
  themeColor: string | null;
  theme: string | null;
  scheduledStart: string | null;
  status: string;
  expiresAt: string | null;
  renewedUntil: string | null;
  requiresPassword: boolean;
  subdomain: string | null;
  isPublic: boolean;
  setupComplete?: boolean;
  destinations?: Destination[];
  /** REPLAY only — see shared/liveStream.ts replayPhase (audit 2026-09-27 M5). */
  replayPhase?: "ready" | "building" | "unavailable" | null;
}

interface Destination {
  id: number;
  platform: string;
  ingestUrl: string;
  enabled: boolean;
  status: string;
  lastError: string | null;
}

interface GuestMessage {
  id: number;
  name: string | null;
  message: string;
  approved: boolean;
  createdAt: string;
}

interface StreamStats {
  totalViews: number;
  uniqueViewers: number;
  chatMessages: number;
  wishes: { total: number; approved: number; pending: number };
  liveViewers: number;
}

interface ScheduleItem {
  time: string;
  label: string;
}


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** stream.schedule may be null, an array, or a JSON string — normalize safely. */
function normalizeSchedule(raw: any): ScheduleItem[] {
  if (!raw) return [];
  let v: any = raw;
  if (typeof v === "string") {
    try { v = JSON.parse(v); } catch { return []; }
  }
  if (!Array.isArray(v)) return [];
  return v
    .filter(x => x && typeof x === "object")
    .map(x => ({ time: String(x.time ?? ""), label: String(x.label ?? "") }))
    .slice(0, 50);
}

function coupleDisplayName(stream: LiveStream): string {
  return stream.coupleNamesOverride?.trim() || "Your Wedding";
}

function watchUrlFor(stream: LiveStream): string {
  return stream.subdomain
    ? `https://${stream.subdomain}.lamirage.live`
    : `https://lamirage.live/w/${stream.slug}`;
}

function coverDisplayUrl(key: string | null): string | null {
  if (!key) return null;
  return key.startsWith("http") || key.startsWith("/") ? key : null;
}

function formatNiceDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

const sharedKey = (id: number) => `lm_live_shared_${id}`;

// ---------------------------------------------------------------------------
// Small shared components
// ---------------------------------------------------------------------------

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button onClick={copy} className="text-gray-500 hover:text-gray-300 transition-colors ml-1" title="Copy">
      {copied ? <Check size={14} className="text-green-400" /> : <Copy size={14} />}
    </button>
  );
}

/** Thin gold hairline used as an editorial section divider. */
function GoldHairline({ className = "" }: { className?: string }) {
  return (
    <div
      className={`h-px w-full ${className}`}
      style={{ background: `linear-gradient(90deg, transparent, ${GOLD}66 20%, ${GOLD}66 80%, transparent)` }}
    />
  );
}

function SectionHeading({ children, sub }: { children: React.ReactNode; sub?: string }) {
  return (
    <div className="mb-4">
      <h3 className="font-playfair text-lg text-white tracking-wide">{children}</h3>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
      <div className="mt-2 h-px w-14" style={{ background: GOLD }} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status stepper — Setup → Scheduled → Live → Replay
// ---------------------------------------------------------------------------

const STEPPER_STEPS = ["Setup", "Scheduled", "Live", "Replay"] as const;

function statusStepIndex(status: string): number {
  switch (status) {
    case "DRAFT": return 0;
    case "SCHEDULED": return 1;
    case "LIVE": return 2;
    case "REPLAY":
    case "EXPIRED": return 3;
    default: return 0;
  }
}

function StatusStepper({ status }: { status: string }) {
  const active = statusStepIndex(status);
  const isLive = status === "LIVE";
  return (
    <div className="flex items-center gap-0 select-none" aria-label={`Stream status: ${status}`}>
      {STEPPER_STEPS.map((label, i) => {
        const done = i < active;
        const current = i === active;
        return (
          <div key={label} className="flex items-center">
            {i > 0 && (
              <div
                className="h-px w-5 sm:w-9 mx-1"
                style={{ background: i <= active ? `${GOLD}99` : "rgba(255,255,255,0.12)" }}
              />
            )}
            <div className="flex items-center gap-1.5">
              <span
                className="relative flex h-2.5 w-2.5 items-center justify-center rounded-full"
                style={{
                  background: current
                    ? (isLive && label === "Live" ? "#ef4444" : GOLD)
                    : done ? GOLD : "rgba(255,255,255,0.15)",
                  boxShadow: current ? `0 0 0 3px ${isLive && label === "Live" ? "rgba(239,68,68,0.25)" : `${GOLD}33`}` : undefined,
                }}
              >
                {current && isLive && label === "Live" && (
                  <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping" />
                )}
              </span>
              <span
                className={`text-[10px] sm:text-[11px] uppercase tracking-[0.14em] ${
                  current ? "text-white font-semibold" : done ? "text-gray-300" : "text-gray-600"
                }`}
              >
                {label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subdomain availability check (debounced) — unchanged logic
// ---------------------------------------------------------------------------

type AvailabilityState = "idle" | "checking" | "available" | "taken" | "invalid";

function useSubdomainAvailability(value: string, currentSubdomain: string | null) {
  const [state, setState] = useState<AvailabilityState>("idle");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const trimmed = value.trim().toLowerCase();

    if (trimmed === (currentSubdomain ?? "")) {
      setState("idle");
      return;
    }

    if (!trimmed) {
      setState("idle");
      return;
    }

    setState("checking");

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const res = await apiRequest(`/api/live-manage/subdomain-available?value=${encodeURIComponent(trimmed)}`);
        if (!res.ok) { setState("invalid"); return; }
        const data = await res.json();
        setState(data.available ? "available" : (data.reason === "invalid" ? "invalid" : "taken"));
      } catch {
        setState("invalid");
      }
    }, 500);

    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [value, currentSubdomain]);

  return state;
}

// ---------------------------------------------------------------------------
// Cover image uploader — unchanged upload flow, restyled shell
// ---------------------------------------------------------------------------

interface CoverImageUploaderProps {
  streamId: number;
  value: string;
  onChange: (url: string) => void;
}

function CoverImageUploader({ streamId, value, onChange }: CoverImageUploaderProps) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const isUrl = value.startsWith("http") || value.startsWith("/");

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid file", description: "Please select an image file (JPG, PNG, WebP).", variant: "destructive" });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "File too large", description: "Please choose an image under 10 MB.", variant: "destructive" });
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const token = localStorage.getItem("token");
      const userStr = localStorage.getItem("user");
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      if (userStr) {
        try {
          const u = JSON.parse(userStr);
          if (u?.id) headers["x-user-id"] = String(u.id);
        } catch {}
      }

      const res = await fetch(`/api/live-manage/${streamId}/cover-upload`, {
        method: "POST",
        headers,
        body: formData,
        credentials: "include",
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Upload failed (${res.status})`);
      }

      const { url } = await res.json();
      onChange(url);
      toast({ title: "Cover uploaded", description: "Save Changes to apply." });
    } catch (err: any) {
      toast({ title: "Upload failed", description: err?.message ?? "Please try again.", variant: "destructive" });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-3">
      {/* Preview */}
      {value && isUrl && (
        <div className="relative rounded-2xl overflow-hidden border border-gray-700/60 bg-gray-800 max-w-md">
          <img
            src={value}
            alt="Cover preview"
            className="w-full h-40 object-cover"
            onError={e => { (e.target as HTMLImageElement).style.display = "none"; }}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-3 py-1 text-xs text-gray-300">
            Current cover
          </div>
        </div>
      )}
      {value && !isUrl && (
        <div className="flex items-center gap-2 rounded-xl border border-gray-700/60 bg-gray-800 px-3 py-2 max-w-md">
          <ImageIcon size={14} className="text-gray-500" />
          <span className="text-xs text-gray-400 font-mono truncate">{value}</span>
        </div>
      )}

      {/* Upload button */}
      <div className="flex items-center gap-3 flex-wrap">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileSelect}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="border-gray-600 text-gray-300 hover:text-white hover:border-gray-400 bg-gray-800 rounded-lg"
        >
          {uploading
            ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Uploading…</>
            : <><Upload size={14} className="mr-1.5" /> Upload cover image</>
          }
        </Button>
        <button
          type="button"
          onClick={() => setShowFallback(v => !v)}
          className="text-xs text-gray-500 hover:text-gray-400 underline underline-offset-2"
        >
          {showFallback ? "Hide" : "Or paste URL / R2 key"}
        </button>
      </div>

      {/* Size guidance */}
      <p className="text-xs text-gray-500">
        Recommended 1920×1080 (16:9 landscape), JPG or PNG, up to 5 MB.
      </p>

      {/* Advanced fallback: manual URL/key entry */}
      {showFallback && (
        <Input
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="live/covers/my-wedding.jpg or https://…"
          className="max-w-md bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cinematic hero header
// ---------------------------------------------------------------------------

function HeroHeader({ stream }: { stream: LiveStream }) {
  const cover = coverDisplayUrl(stream.coverImageKey);
  const watchUrl = watchUrlFor(stream);
  const hasSubdomain = !!stream.subdomain;
  const dateStr = formatNiceDate(stream.scheduledStart);

  return (
    <div className="relative overflow-hidden rounded-2xl border border-gray-700/60">
      {/* Backdrop */}
      <div className="absolute inset-0">
        {cover ? (
          <img
            src={cover}
            alt=""
            aria-hidden
            className="h-full w-full object-cover scale-105 blur-[2px] opacity-40"
            onError={e => { (e.target as HTMLImageElement).style.display = "none"; }}
          />
        ) : (
          <div
            className="h-full w-full"
            style={{ background: `radial-gradient(120% 140% at 15% 0%, #2a2415 0%, #14120c 45%, #0b0b0d 100%)` }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/70 to-gray-900/30" />
      </div>

      {/* Content */}
      <div className="relative px-5 py-7 sm:px-8 sm:py-9">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gray-400 mb-2">
              <Radio size={13} style={{ color: GOLD }} />
              LA Mirage Live Studio
            </p>
            <h1 className="font-playfair text-3xl sm:text-4xl text-white leading-tight truncate">
              {coupleDisplayName(stream)}
            </h1>
            {dateStr && (
              <p className="mt-1.5 text-sm text-gray-300 flex items-center gap-1.5">
                <CalendarClock size={13} className="text-gray-500" />
                {dateStr}
              </p>
            )}
            <div className="mt-5">
              <StatusStepper status={stream.status} />
            </div>
          </div>

          <div className="flex-shrink-0">
            {hasSubdomain ? (
              <a href={watchUrl} target="_blank" rel="noopener noreferrer">
                <Button
                  className="text-gray-900 font-semibold hover:opacity-90 rounded-xl px-5"
                  style={{ background: GOLD_GRAD }}
                >
                  View guest page <ExternalLink size={14} className="ml-1.5" />
                </Button>
              </a>
            ) : (
              <span title="Choose your web address in the Presentation tab first">
                <Button
                  disabled
                  className="text-gray-900 font-semibold rounded-xl px-5 opacity-50 cursor-not-allowed"
                  style={{ background: GOLD_GRAD }}
                >
                  View guest page <ExternalLink size={14} className="ml-1.5" />
                </Button>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Gold hairline base */}
      <div className="relative">
        <GoldHairline />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Overview tab — stats, checklist, share & invite
// ---------------------------------------------------------------------------

function StatCard({
  label, value, accent, badge, pulsing,
}: {
  label: string;
  value: number | string;
  accent?: boolean;
  badge?: string | null;
  pulsing?: boolean;
}) {
  return (
    <div className="relative rounded-2xl border border-gray-700/60 bg-gray-800/60 px-4 pt-4 pb-3 overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />
      <div className="flex items-baseline gap-2">
        <span
          className="font-playfair text-2xl sm:text-3xl leading-none"
          style={{ color: accent ? GOLD : "#fff" }}
        >
          {value}
        </span>
        {pulsing && (
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
          </span>
        )}
        {badge && (
          <span className="ml-auto rounded-full bg-amber-900/50 border border-amber-700/50 px-1.5 py-0.5 text-[10px] font-medium text-amber-300 whitespace-nowrap">
            {badge}
          </span>
        )}
      </div>
      <p className="mt-1.5 text-[10px] uppercase tracking-[0.16em] text-gray-500">{label}</p>
    </div>
  );
}

interface ChecklistItem {
  id: string;
  label: string;
  done: boolean;
  actionLabel: string;
  action: () => void;
}

function OverviewTab({
  stream,
  onNavigate,
}: {
  stream: LiveStream;
  onNavigate: (tab: PortalTab) => void;
}) {
  const { toast } = useToast();
  const watchUrl = watchUrlFor(stream);
  const hasSubdomain = !!stream.subdomain;
  const names = coupleDisplayName(stream);
  const qrRef = useRef<HTMLDivElement>(null);
  const inviteRef = useRef<HTMLDivElement>(null);

  // "Shared with guests" — persisted per stream in localStorage
  const [shared, setShared] = useState(false);
  useEffect(() => {
    try { setShared(localStorage.getItem(sharedKey(stream.id)) === "1"); } catch { setShared(false); }
  }, [stream.id]);
  const markShared = () => {
    setShared(true);
    try { localStorage.setItem(sharedKey(stream.id), "1"); } catch {}
  };

  // Stats — poll faster while LIVE
  const { data: stats } = useQuery<StreamStats>({
    queryKey: [`/api/live-manage/${stream.id}/stats`],
    queryFn: () => apiRequest(`/api/live-manage/${stream.id}/stats`).then(r => r.json()),
    refetchInterval: stream.status === "LIVE" ? 10000 : 30000,
  });

  const scheduleItems = useMemo(() => normalizeSchedule(stream.schedule), [stream.schedule]);

  const checklist: ChecklistItem[] = [
    {
      id: "subdomain",
      label: "Web address chosen",
      done: hasSubdomain,
      actionLabel: "Do it",
      action: () => onNavigate("presentation"),
    },
    {
      id: "cover",
      label: "Cover photo added",
      done: !!stream.coverImageKey,
      actionLabel: "Do it",
      action: () => onNavigate("presentation"),
    },
    {
      id: "theme",
      label: "Theme picked",
      done: !!(stream.theme ?? "midnight"),
      actionLabel: "Do it",
      action: () => onNavigate("presentation"),
    },
    {
      id: "welcome",
      label: "Welcome message written",
      done: !!stream.welcomeMessage?.trim(),
      actionLabel: "Do it",
      action: () => onNavigate("presentation"),
    },
    {
      id: "schedule",
      label: "Order of events added",
      done: scheduleItems.length > 0,
      actionLabel: "Do it",
      action: () => onNavigate("presentation"),
    },
    {
      id: "shared",
      label: "Shared with guests",
      done: shared,
      actionLabel: "Do it",
      action: () => inviteRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }),
    },
  ];
  const doneCount = checklist.filter(c => c.done).length;
  const progressPct = Math.round((doneCount / checklist.length) * 100);

  const copyLink = () => {
    navigator.clipboard.writeText(watchUrl).then(() => {
      toast({ title: "Link copied", description: "Share it with your guests." });
      markShared();
    });
  };

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(
    `Dear friends & family — ${names} are getting married! 💛 Join us and watch the celebration live at LA Mirage: ${watchUrl}`
  )}`;

  const downloadQr = () => {
    const svg = qrRef.current?.querySelector("svg");
    if (!svg) return;
    try {
      const xml = new XMLSerializer().serializeToString(svg);
      const svg64 = window.btoa(unescape(encodeURIComponent(xml)));
      const img = new Image();
      img.onload = () => {
        const size = 640;
        const pad = 48;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, size, size);
        ctx.drawImage(img, pad, pad, size - pad * 2, size - pad * 2);
        const a = document.createElement("a");
        a.href = canvas.toDataURL("image/png");
        a.download = `${stream.subdomain || stream.slug}-live-qr.png`;
        a.click();
      };
      img.src = `data:image/svg+xml;base64,${svg64}`;
      markShared();
    } catch {
      toast({ title: "Download failed", description: "Could not generate the QR image.", variant: "destructive" });
    }
  };

  // "available until" only for a film that exists (M5) — never for one still building or lost.
  const replayUntil = (stream.status === "REPLAY" && (!stream.replayPhase || stream.replayPhase === "ready")) || stream.status === "EXPIRED"
    ? formatNiceDate(stream.renewedUntil ?? stream.expiresAt)
    : null;

  return (
    <div className="space-y-8">
      {/* Stats */}
      <div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <StatCard
            label="Live now"
            value={stats?.liveViewers ?? 0}
            accent
            pulsing={(stats?.liveViewers ?? 0) > 0}
          />
          <StatCard label="Total views" value={stats?.totalViews ?? 0} />
          <StatCard label="Unique guests" value={stats?.uniqueViewers ?? 0} />
          <StatCard
            label="Wishes"
            value={stats?.wishes?.total ?? 0}
            badge={(stats?.wishes?.pending ?? 0) > 0 ? `${stats!.wishes.pending} awaiting` : null}
          />
          <StatCard label="Chat messages" value={stats?.chatMessages ?? 0} />
        </div>
        {replayUntil && (
          <p className="mt-3 text-xs text-gray-500 flex items-center gap-1.5">
            <PlayCircle size={13} className="text-gray-600" />
            Replay available until {replayUntil}
          </p>
        )}
      </div>

      {/* Setup checklist */}
      <div className="rounded-2xl border border-gray-700/60 bg-gray-800/40 p-5 sm:p-6">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div>
            <h3 className="font-playfair text-lg text-white">Your stream journey</h3>
            <p className="text-xs text-gray-500 mt-0.5">A few elegant touches before the big day.</p>
          </div>
          <span className="font-playfair text-2xl" style={{ color: GOLD }}>{progressPct}%</span>
        </div>
        <div className="h-1 rounded-full bg-gray-700/60 overflow-hidden mb-5">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{ width: `${progressPct}%`, background: GOLD_GRAD }}
          />
        </div>
        <ul className="space-y-1">
          {checklist.map(item => (
            <li key={item.id} className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-gray-800/60 transition-colors">
              <span
                className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border"
                style={item.done
                  ? { background: GOLD, borderColor: GOLD }
                  : { borderColor: "rgba(255,255,255,0.2)" }}
              >
                {item.done && <Check size={12} className="text-gray-900" strokeWidth={3} />}
              </span>
              <span className={`text-sm ${item.done ? "text-gray-400" : "text-gray-200"}`}>{item.label}</span>
              {!item.done && (
                <button
                  onClick={item.action}
                  className="ml-auto flex items-center gap-0.5 text-xs font-medium hover:opacity-80 transition-opacity"
                  style={{ color: GOLD }}
                >
                  {item.actionLabel} <ChevronRight size={12} />
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* Share & invite */}
      <div ref={inviteRef}>
        {hasSubdomain ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Share2 size={15} style={{ color: GOLD }} />
              <h3 className="font-playfair text-lg text-white">Invite your guests</h3>
            </div>

            {/* Framed invite card */}
            <div
              className="relative rounded-2xl p-[1px] overflow-hidden"
              style={{ background: `linear-gradient(135deg, ${GOLD}, ${GOLD}33 40%, ${GOLD}33 60%, ${GOLD})` }}
            >
              <div className="rounded-2xl bg-gray-900 px-5 py-6 sm:px-8 sm:py-7">
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  <div className="flex-1 text-center sm:text-left min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.28em] text-gray-500 mb-2">You are invited</p>
                    <p className="font-playfair text-2xl sm:text-3xl text-white leading-snug break-words">{names}</p>
                    <p className="mt-1 text-sm" style={{ color: GOLD }}>are getting married — watch live</p>
                    <div className="mt-4 mx-auto sm:mx-0 h-px w-24" style={{ background: `${GOLD}66` }} />
                    <p className="mt-3 text-xs sm:text-sm font-mono text-gray-300 break-all flex items-center justify-center sm:justify-start gap-1">
                      {watchUrl}
                      <CopyButton value={watchUrl} />
                    </p>
                  </div>
                  <div ref={qrRef} className="flex-shrink-0 rounded-xl bg-white p-2 shadow-lg">
                    <QRCode value={watchUrl} size={112} />
                  </div>
                </div>
              </div>
            </div>

            {/* Share actions */}
            <div className="flex flex-wrap gap-2.5">
              <Button
                onClick={copyLink}
                className="text-gray-900 font-semibold hover:opacity-90 rounded-xl"
                style={{ background: GOLD_GRAD }}
              >
                <Copy size={14} className="mr-1.5" /> Copy link
              </Button>
              <a href={whatsappHref} target="_blank" rel="noopener noreferrer" onClick={markShared}>
                <Button
                  variant="outline"
                  className="border-green-700/70 bg-green-900/20 text-green-300 hover:bg-green-900/40 hover:text-green-200 rounded-xl"
                >
                  <MessageSquare size={14} className="mr-1.5" /> WhatsApp
                </Button>
              </a>
              <Button
                variant="outline"
                onClick={downloadQr}
                className="border-gray-600 bg-gray-800 text-gray-300 hover:text-white hover:border-gray-400 rounded-xl"
              >
                <Download size={14} className="mr-1.5" /> Download QR
              </Button>
            </div>
            <p className="text-xs text-gray-600">
              Print the QR on your invitation cards — guests scan it to join the live stream instantly.
            </p>
          </div>
        ) : (
          <button
            onClick={() => onNavigate("presentation")}
            className="w-full rounded-2xl border border-dashed px-5 py-6 text-left transition-colors hover:bg-gray-800/40"
            style={{ borderColor: `${GOLD}66` }}
          >
            <div className="flex items-center gap-3">
              <QrCode size={22} style={{ color: GOLD }} />
              <div>
                <p className="text-sm font-semibold text-white">Choose your web address first →</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Pick a beautiful link like <span className="font-mono">priya-arjun.lamirage.live</span>, then share it with a QR code and WhatsApp invite.
                </p>
              </div>
            </div>
          </button>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Presentation editor
// ---------------------------------------------------------------------------

function PresentationTab({ stream }: { stream: LiveStream }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState({
    coupleNamesOverride: stream.coupleNamesOverride ?? "",
    welcomeMessage: stream.welcomeMessage ?? "",
    themeColor: stream.themeColor ?? "#c9a962",
    theme: stream.theme ?? "midnight",
    coverImageKey: stream.coverImageKey ?? "",
    guestPassword: "",
    subdomain: stream.subdomain ?? "",
    isPublic: stream.isPublic ?? false,
  });
  const [schedule, setSchedule] = useState<ScheduleItem[]>(() => normalizeSchedule(stream.schedule));
  const [showPw, setShowPw] = useState(false);
  const [subdomainError, setSubdomainError] = useState<string | null>(null);

  const availState = useSubdomainAvailability(form.subdomain, stream.subdomain);

  const patch = useMutation({
    mutationFn: (body: any) =>
      apiRequest(`/api/live-manage/${stream.id}`, { method: "PATCH", data: body }),
    onSuccess: () => {
      toast({ title: "Saved", description: "Your stream details have been updated." });
      qc.invalidateQueries({ queryKey: ["/api/live-manage"] });
      setForm(f => ({ ...f, guestPassword: "" }));
      setSubdomainError(null);
    },
    onError: async (err: any) => {
      const status = err?.status ?? err?.response?.status;
      if (status === 409) {
        setSubdomainError("That subdomain is already taken. Please choose another.");
      } else if (status === 400) {
        setSubdomainError("Invalid subdomain. Use lowercase letters, numbers, and hyphens only (3-30 chars).");
      } else {
        toast({ title: "Error", description: err?.message ?? "Failed to save", variant: "destructive" });
      }
    },
  });

  const handleSave = () => {
    if (form.subdomain.trim() && (availState === "taken" || availState === "invalid")) {
      toast({ title: "Subdomain issue", description: "Please fix the subdomain before saving.", variant: "destructive" });
      return;
    }

    const cleanSchedule = schedule
      .map(row => ({ time: row.time.trim(), label: row.label.trim() }))
      .filter(row => row.time || row.label)
      .slice(0, 50);

    const body: any = {
      coupleNamesOverride: form.coupleNamesOverride || null,
      welcomeMessage: form.welcomeMessage || null,
      themeColor: form.themeColor,
      theme: form.theme,
      coverImageKey: form.coverImageKey || null,
      isPublic: form.isPublic,
      schedule: cleanSchedule,
    };
    if (form.subdomain.trim()) {
      body.subdomain = form.subdomain.trim().toLowerCase();
    } else if (stream.subdomain) {
      body.subdomain = null;
    }
    if (form.guestPassword.trim().length >= 3) {
      body.guestPassword = form.guestPassword;
    }
    setSubdomainError(null);
    patch.mutate(body);
  };

  const availIndicator = () => {
    const trimmed = form.subdomain.trim();
    if (!trimmed || trimmed === (stream.subdomain ?? "")) return null;
    if (availState === "checking") return <span className="text-xs text-gray-500 ml-1">Checking…</span>;
    if (availState === "available") return <span className="text-xs text-green-400 ml-1 flex items-center gap-0.5"><Check size={11} /> Available</span>;
    if (availState === "taken") return <span className="text-xs text-red-400 ml-1">Already taken</span>;
    if (availState === "invalid") return <span className="text-xs text-red-400 ml-1">Invalid format</span>;
    return null;
  };

  const subdomainPreview = form.subdomain.trim()
    ? `https://${form.subdomain.trim().toLowerCase()}.lamirage.live`
    : null;

  const updateScheduleRow = (idx: number, field: keyof ScheduleItem, value: string) => {
    setSchedule(rows => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  return (
    <div className="space-y-8">
      {/* --- Story section ------------------------------------------------ */}
      <div>
        <SectionHeading sub="How your names and greeting appear to guests.">Your story</SectionHeading>
        <div className="space-y-5">
          <div>
            <Label className="text-sm text-gray-400 mb-1.5 block">Couple Names (displayed on the live page)</Label>
            <Input
              value={form.coupleNamesOverride}
              onChange={e => setForm(f => ({ ...f, coupleNamesOverride: e.target.value }))}
              placeholder="e.g. Priya & Arjun"
              className="max-w-md bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
            />
          </div>

          <div>
            <Label className="text-sm text-gray-400 mb-1.5 block">Welcome Message</Label>
            <Textarea
              value={form.welcomeMessage}
              onChange={e => setForm(f => ({ ...f, welcomeMessage: e.target.value }))}
              placeholder="A short message to greet your guests…"
              rows={3}
              className="max-w-lg bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
            />
          </div>

          <div>
            <Label className="text-sm text-gray-400 mb-1.5 block">Cover Image</Label>
            <CoverImageUploader
              streamId={stream.id}
              value={form.coverImageKey}
              onChange={url => setForm(f => ({ ...f, coverImageKey: url }))}
            />
          </div>
        </div>
      </div>

      <GoldHairline />

      {/* --- Order of events ---------------------------------------------- */}
      <div>
        <SectionHeading sub="This becomes the elegant 'Order of Events' timeline your guests see.">
          Order of events
        </SectionHeading>
        <div className="space-y-2.5 max-w-xl">
          {schedule.length === 0 && (
            <p className="text-xs text-gray-600 italic">
              No moments yet — add your ceremony milestones below.
            </p>
          )}
          {schedule.map((row, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <Input
                value={row.time}
                onChange={e => updateScheduleRow(idx, "time", e.target.value)}
                placeholder="6:00 PM"
                className="w-28 sm:w-32 flex-shrink-0 bg-gray-800 border-gray-700 text-white placeholder:text-gray-600 text-sm"
              />
              <Input
                value={row.label}
                onChange={e => updateScheduleRow(idx, "label", e.target.value)}
                placeholder="Reception begins"
                className="flex-1 bg-gray-800 border-gray-700 text-white placeholder:text-gray-600 text-sm"
              />
              <button
                type="button"
                onClick={() => setSchedule(rows => rows.filter((_, i) => i !== idx))}
                className="flex-shrink-0 rounded-lg p-1.5 text-gray-500 hover:text-red-400 hover:bg-gray-800 transition-colors"
                title="Remove"
              >
                <X size={15} />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setSchedule(rows => [...rows, { time: "", label: "" }])}
            disabled={schedule.length >= 50}
            className="flex items-center gap-1.5 text-sm font-medium mt-1 disabled:opacity-40 hover:opacity-80 transition-opacity"
            style={{ color: GOLD }}
          >
            <Plus size={14} /> Add moment
          </button>
        </div>
      </div>

      <GoldHairline />

      {/* --- Look & feel --------------------------------------------------- */}
      <div>
        <SectionHeading sub="Choose the mood of your guest watch page.">Look &amp; feel</SectionHeading>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl">
          {LIVE_THEMES.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setForm(prev => ({ ...prev, theme: t.id }))}
              className={`relative rounded-2xl border p-3.5 text-left transition-all ${
                form.theme === t.id
                  ? "shadow-md"
                  : "border-gray-700/60 hover:border-gray-500"
              }`}
              style={form.theme === t.id ? { borderColor: GOLD, boxShadow: `0 0 0 1px ${GOLD}` } : undefined}
            >
              <div className="flex gap-1.5 mb-2.5">
                <div className="h-6 w-6 rounded-full" style={{ background: t.swatch[0], border: "1px solid rgba(255,255,255,0.12)" }} />
                <div className="h-6 w-6 rounded-full -ml-2.5" style={{ background: t.swatch[1], border: "1px solid rgba(0,0,0,0.25)" }} />
              </div>
              <p className="text-xs font-medium text-gray-300">{t.name}</p>
              {form.theme === t.id && (
                <div className="absolute top-2.5 right-2.5 flex h-4 w-4 items-center justify-center rounded-full" style={{ background: GOLD }}>
                  <Check size={10} className="text-gray-900" strokeWidth={3.5} />
                </div>
              )}
            </button>
          ))}
        </div>

        <div className="mt-5">
          <Label className="text-sm text-gray-400 mb-1.5 block">Theme Accent Colour</Label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={form.themeColor}
              onChange={e => setForm(f => ({ ...f, themeColor: e.target.value }))}
              className="h-9 w-16 rounded border border-gray-700 cursor-pointer p-0.5 bg-gray-800"
            />
            <span className="text-sm text-gray-500">{form.themeColor}</span>
          </div>
        </div>
      </div>

      <GoldHairline />

      {/* --- Address & privacy --------------------------------------------- */}
      <div>
        <SectionHeading sub="Your personal link, visibility, and guest password.">Address &amp; privacy</SectionHeading>

        <div className="space-y-5">
          <div>
            <Label className="text-sm text-gray-400 mb-1.5 flex items-center gap-1.5">
              <LinkIcon size={13} className="text-gray-500" />
              Custom Stream URL
              <span className="text-gray-600 text-xs ml-1">(optional — lowercase, letters/numbers/hyphens)</span>
            </Label>
            <div className="flex items-center gap-2 max-w-md">
              <span className="text-sm text-gray-500 whitespace-nowrap">https://</span>
              <Input
                value={form.subdomain}
                onChange={e => {
                  setSubdomainError(null);
                  setForm(f => ({ ...f, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }));
                }}
                placeholder="priya-arjun"
                className="flex-1 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                maxLength={30}
                autoComplete="off"
              />
              <span className="text-sm text-gray-500 whitespace-nowrap">.lamirage.live</span>
            </div>
            <div className="flex items-center gap-2 mt-1 min-h-[18px]">
              {availIndicator()}
              {subdomainPreview && availState !== "taken" && availState !== "invalid" && (
                <span className="text-xs text-gray-600 flex items-center gap-0.5">
                  <Globe size={10} /> {subdomainPreview}
                </span>
              )}
            </div>
            {subdomainError && <p className="text-xs text-red-400 mt-1">{subdomainError}</p>}
          </div>

          <div className="flex items-center justify-between max-w-md rounded-2xl border border-gray-700/60 bg-gray-800/60 px-4 py-3">
            <div>
              <p className="text-sm font-medium text-white">Show on LA Mirage Live page</p>
              <p className="text-xs text-gray-500 mt-0.5">
                When enabled, your wedding appears in the public "what's live now" list at lamirage.live/live.
              </p>
            </div>
            <Switch
              checked={form.isPublic}
              onCheckedChange={checked => setForm(f => ({ ...f, isPublic: checked }))}
              className="ml-4 flex-shrink-0"
            />
          </div>

          <div>
            <Label className="text-sm text-gray-400 mb-1.5 block">
              Guest Password{" "}
              <span className="text-gray-600 text-xs ml-1">(leave blank to keep existing, min 3 chars to set/change)</span>
            </Label>
            <div className="relative max-w-xs">
              <Input
                type={showPw ? "text" : "password"}
                value={form.guestPassword}
                onChange={e => setForm(f => ({ ...f, guestPassword: e.target.value }))}
                placeholder={stream.requiresPassword ? "••••• (set — enter to change)" : "No password set"}
                className="pr-9 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowPw(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
              >
                {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
            {stream.requiresPassword && (
              <p className="text-xs text-green-400 mt-1 flex items-center gap-1">
                <Check size={12} /> Password protection is active
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="pt-1">
        <Button
          onClick={handleSave}
          disabled={patch.isPending}
          style={{ background: GOLD_GRAD }}
          className="text-gray-900 font-semibold hover:opacity-90 rounded-xl px-6"
        >
          {patch.isPending ? "Saving…" : "Save Changes"}
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Social connect wizard (Restream-style guided flow)
// ---------------------------------------------------------------------------

type SocialPlatform = "youtube" | "facebook";

const SOCIAL_PLATFORMS: Record<SocialPlatform, {
  label: string;
  accent: string;          // accent color for icon / ring
  accentSoft: string;      // low-alpha bg for the icon circle
  ingestUrl: string;       // auto-filled — the couple never sees RTMP
  link: string;
  linkLabel: string;
  blurb: string;           // card description
  step1Title: string;
  step1Caption: React.ReactNode;
  step2Title: string;
  step2Caption: React.ReactNode;
  successText: string;
}> = {
  youtube: {
    label: "YouTube",
    accent: "#ef4444",
    accentSoft: "rgba(239,68,68,0.12)",
    ingestUrl: "rtmp://a.rtmp.youtube.com/live2",
    link: "https://studio.youtube.com/",
    linkLabel: "Open YouTube Studio",
    blurb: "Stream to your own YouTube channel so family anywhere can watch.",
    step1Title: "Turn on live streaming — do this the day before",
    step1Caption: (
      <>Sign in with the account where the wedding should stream. If this channel has never gone
        live, YouTube must <span className="text-white font-medium">enable live streaming</span> first —
        it needs a verified phone number and <span className="text-white font-medium">can take up to 24 hours</span>,
        so set it up at least a day ahead. Then click{" "}
        <span className="text-white font-medium">Create → Go live → Now</span>.</>
    ),
    step2Title: "Pick ‘Streaming software’ + turn on Auto-start",
    step2Caption: (
      <>Skip webcam — choose <span className="text-white font-medium">Streaming software</span>. In the
        settings, switch on <span className="text-white font-medium">Auto-start</span> so it goes live by
        itself on the day. Your <span className="text-white font-medium">Stream key</span> is shown under
        “Stream settings”.</>
    ),
    successText:
      "YouTube connected — on the wedding day we push your celebration to your channel automatically, and with Auto-start on it goes live with no clicks. YouTube reuses this stream key, so you're set.",
  },
  facebook: {
    label: "Facebook",
    accent: "#3b82f6",
    accentSoft: "rgba(59,130,246,0.12)",
    ingestUrl: "rtmps://live-api-s.facebook.com:443/rtmp/",
    link: "https://www.facebook.com/live/producer?ref=lamirage",
    linkLabel: "Open Facebook Live",
    blurb: "Go live on your Facebook profile or page for friends to join in.",
    step1Title: "Open Live Producer → Streaming software",
    step1Caption: (
      <>On facebook.com open <span className="text-white font-medium">Live Producer</span> and choose{" "}
        <span className="text-white font-medium">Streaming software</span>.</>
    ),
    step2Title: "Turn on a Persistent Stream Key, then copy it",
    step2Caption: (
      <>Below the preview, open <span className="text-white font-medium">“Use Stream Key”</span> and tick{" "}
        <span className="text-white font-medium">“Use a persistent stream key”</span> — otherwise Facebook’s
        key expires before the wedding. Then <span className="text-white font-medium">Copy</span> the Stream key.</>
    ),
    successText:
      "Facebook connected — we push your celebration to Facebook automatically on the day. Because Facebook needs a broadcast to be started, either schedule your Live with “Go live automatically”, or just tap Go Live on your post when we message you it's streaming.",
  },
};

/** Soft (non-blocking) sanity check on a pasted stream key. Returns a warning or null. */
function socialKeyWarning(platform: SocialPlatform, key: string): string | null {
  const k = key.trim();
  if (!k) return null;
  if (/^(https?|rtmps?):\/\//i.test(k) || k.includes("://")) {
    return "That looks like a URL — paste the Stream key itself, not the stream URL.";
  }
  if (platform === "youtube") {
    if (!/^[a-z0-9]{4}(-[a-z0-9]{4}){3,5}$/i.test(k)) {
      return "This doesn't look like a YouTube stream key — double-check you copied the Stream key, not the URL.";
    }
    return null;
  }
  // Facebook keys are long strings of digits + letters (often with dashes) — never spaces.
  if (/\s/.test(k)) {
    return "Stream keys never contain spaces — double-check you copied the whole key and nothing extra.";
  }
  return null;
}

function PlatformIcon({ platform, size = 18 }: { platform: SocialPlatform; size?: number }) {
  const meta = SOCIAL_PLATFORMS[platform];
  return platform === "youtube"
    ? <Youtube size={size} style={{ color: meta.accent }} />
    : <Facebook size={size} style={{ color: meta.accent }} />;
}

function DestinationStatusBadge({ d }: { d: Destination }) {
  return (
    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium capitalize ${
      d.status === "live" ? "bg-green-900/60 text-green-400" :
      d.status === "error" ? "bg-red-900/60 text-red-400" :
      "bg-gray-700 text-gray-400"
    }`}>
      {d.status === "live" ? "Live now" : d.status}
    </span>
  );
}

/** Gold numbered circle + connector line, one per wizard step. */
function WizardStep({
  n, last, title, children,
}: { n: number; last?: boolean; title: string; children: React.ReactNode }) {
  return (
    <div className="relative flex gap-4 pb-7 last:pb-0">
      {!last && (
        <div
          className="absolute left-[15px] top-9 bottom-0 w-px"
          style={{ background: `linear-gradient(180deg, ${GOLD}55, ${GOLD}22)` }}
        />
      )}
      <div
        className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-semibold z-10"
        style={{ background: GOLD_GRAD, color: "#111827", boxShadow: `0 0 0 4px ${GOLD}1a` }}
      >
        {n}
      </div>
      <div className="flex-1 min-w-0 pt-1">
        <p className="font-playfair text-[15px] text-white tracking-wide">{title}</p>
        <div className="mt-1.5 text-sm text-gray-400 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function SocialKeysTab({ stream, destinations }: { stream: LiveStream; destinations: Destination[] }) {
  const { toast } = useToast();
  const qc = useQueryClient();

  // Wizard state
  const [wizardPlatform, setWizardPlatform] = useState<SocialPlatform | null>(null);
  const [streamKey, setStreamKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [justConnected, setJustConnected] = useState<SocialPlatform | null>(null);

  const destFor = (p: SocialPlatform) => destinations.find(d => d.platform === p);

  const openWizard = (p: SocialPlatform) => {
    setWizardPlatform(p);
    setStreamKey("");
    setShowKey(false);
    setJustConnected(null);
  };

  const closeWizard = () => {
    setWizardPlatform(null);
    setStreamKey("");
  };

  const upsert = useMutation({
    mutationFn: (body: { platform: SocialPlatform; ingestUrl: string; streamKey: string; enabled: boolean }) =>
      apiRequest(`/api/live-manage/${stream.id}/destination`, { method: "PUT", data: body }),
    onSuccess: (_res, vars) => {
      toast({
        title: vars.enabled ? `${SOCIAL_PLATFORMS[vars.platform].label} connected` : `${SOCIAL_PLATFORMS[vars.platform].label} paused`,
        description: "Your key is encrypted and stored securely.",
      });
      qc.invalidateQueries({ queryKey: ["/api/live-manage"] });
      setJustConnected(vars.enabled ? vars.platform : null);
      setWizardPlatform(null);
      setStreamKey("");
    },
    onError: (err: any) => {
      toast({ title: "Couldn't save", description: err?.message ?? "Failed to save — please try again.", variant: "destructive" });
    },
  });

  // One-tap pause/resume — no key required (PATCH flips `enabled` server-side).
  const toggle = useMutation({
    mutationFn: ({ did, enabled }: { did: number; platform: SocialPlatform; enabled: boolean }) =>
      apiRequest(`/api/live-manage/${stream.id}/destination/${did}`, { method: "PATCH", data: { enabled } }),
    onSuccess: (_res, vars) => {
      toast({
        title: vars.enabled
          ? `${SOCIAL_PLATFORMS[vars.platform].label} streaming on`
          : `${SOCIAL_PLATFORMS[vars.platform].label} paused`,
        description: vars.enabled
          ? "We'll stream to this account when your wedding goes live."
          : "We won't stream to this account until you turn it back on.",
      });
      qc.invalidateQueries({ queryKey: ["/api/live-manage"] });
    },
    onError: (err: any) => {
      toast({ title: "Couldn't update", description: err?.message ?? "Failed to update — please try again.", variant: "destructive" });
    },
  });

  const handleSave = () => {
    if (!wizardPlatform) return;
    const k = streamKey.trim();
    if (!k) {
      toast({ title: "Paste your Stream key", description: "The Stream key from step 2 is required.", variant: "destructive" });
      return;
    }
    const existing = destFor(wizardPlatform);
    upsert.mutate({
      platform: wizardPlatform,
      ingestUrl: SOCIAL_PLATFORMS[wizardPlatform].ingestUrl, // auto — the couple never touches RTMP
      streamKey: k,
      enabled: existing ? existing.enabled : true, // replacing a key keeps the current on/off state
    });
  };

  const warning = wizardPlatform ? socialKeyWarning(wizardPlatform, streamKey) : null;
  const meta = wizardPlatform ? SOCIAL_PLATFORMS[wizardPlatform] : null;

  return (
    <div className="space-y-8">
      {/* Explainer */}
      <div className="rounded-2xl border border-gray-700/60 bg-gray-800/40 px-5 py-4 flex items-start gap-3">
        <Sparkles size={16} className="mt-0.5 flex-shrink-0" style={{ color: GOLD }} />
        <p className="text-sm text-gray-300 leading-relaxed">
          We stream your celebration straight to your own YouTube or Facebook. Do the one-time
          connect below (best done a few days ahead) and we run the broadcast for you on the day.
        </p>
      </div>

      {/* Success banner */}
      {justConnected && (
        <div
          className="rounded-2xl border px-5 py-4 flex items-start gap-3"
          style={{ borderColor: `${GOLD}55`, background: `${GOLD}0d` }}
        >
          <span
            className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full"
            style={{ background: GOLD_GRAD }}
          >
            <Check size={14} className="text-gray-900" />
          </span>
          <div className="min-w-0">
            <p className="font-playfair text-white">
              {SOCIAL_PLATFORMS[justConnected].label} connected
            </p>
            <p className="text-sm text-gray-400 mt-1 leading-relaxed">
              {SOCIAL_PLATFORMS[justConnected].successText}
            </p>
          </div>
          <button onClick={() => setJustConnected(null)} className="ml-auto text-gray-500 hover:text-gray-300" title="Dismiss">
            <X size={15} />
          </button>
        </div>
      )}

      {/* Platform chooser */}
      <div>
        <SectionHeading sub="Choose where your celebration should stream. You can connect both.">
          Connect your accounts
        </SectionHeading>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {(Object.keys(SOCIAL_PLATFORMS) as SocialPlatform[]).map(p => {
            const pm = SOCIAL_PLATFORMS[p];
            const dest = destFor(p);
            const isOpen = wizardPlatform === p;
            return (
              <div
                key={p}
                className="rounded-2xl border bg-gray-800/60 p-5 transition-all"
                style={{
                  borderColor: isOpen ? `${GOLD}88` : dest ? "rgba(74,222,128,0.25)" : "rgba(75,85,99,0.6)",
                  boxShadow: isOpen ? `0 0 0 1px ${GOLD}44` : undefined,
                }}
              >
                <div className="flex items-start gap-3">
                  <span
                    className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full"
                    style={{ background: pm.accentSoft, boxShadow: `inset 0 0 0 1px ${pm.accent}33` }}
                  >
                    <PlatformIcon platform={p} size={20} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-playfair text-white text-base">{pm.label}</p>
                      {dest ? (
                        <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-green-900/50 text-green-400 font-medium">
                          <Check size={11} /> Connected
                        </span>
                      ) : null}
                    </div>
                    <p className="text-xs text-gray-500 mt-1 leading-relaxed">{pm.blurb}</p>
                  </div>
                </div>

                {dest ? (
                  <div className="mt-4 space-y-2.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <DestinationStatusBadge d={dest} />
                      {!dest.enabled && <span className="text-[11px] text-gray-500 italic">paused</span>}
                      {dest.lastError && (
                        <span className="text-[11px] text-red-400 truncate max-w-[220px]" title={dest.lastError}>
                          {dest.lastError}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
                        <Switch
                          checked={dest.enabled}
                          disabled={toggle.isPending}
                          onCheckedChange={(next) => toggle.mutate({ did: dest.id, platform: p, enabled: next })}
                        />
                        {dest.enabled ? "Streaming on" : "Streaming off"}
                      </label>
                      <button
                        onClick={() => openWizard(p)}
                        className="text-xs font-medium transition-colors hover:opacity-80 inline-flex items-center gap-1"
                        style={{ color: GOLD }}
                      >
                        <Key size={11} /> Replace key
                      </button>
                    </div>
                  </div>
                ) : (
                  <Button
                    onClick={() => openWizard(p)}
                    className="mt-4 w-full rounded-xl text-gray-900 font-semibold hover:opacity-90"
                    style={{ background: GOLD_GRAD }}
                  >
                    Connect {pm.label} <ChevronRight size={15} className="ml-1" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Guided wizard */}
      {wizardPlatform && meta && (
        <div className="rounded-2xl border border-gray-700/60 bg-gray-800/40 p-5 sm:p-6">
          <div className="flex items-center gap-3 mb-6">
            <span
              className="flex h-9 w-9 items-center justify-center rounded-full"
              style={{ background: meta.accentSoft }}
            >
              <PlatformIcon platform={wizardPlatform} size={17} />
            </span>
            <h3 className="font-playfair text-lg text-white tracking-wide flex-1">
              {destFor(wizardPlatform) ? `Update ${meta.label}` : `Connect ${meta.label}`}
            </h3>
            <button onClick={closeWizard} className="text-gray-500 hover:text-gray-300" title="Close">
              <X size={17} />
            </button>
          </div>

          <div>
            <WizardStep n={1} title={meta.step1Title}>
              <a
                href={meta.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-semibold text-gray-900 hover:opacity-90 transition-opacity"
                style={{ background: GOLD_GRAD }}
              >
                {meta.linkLabel} <ExternalLink size={13} />
              </a>
              <p className="mt-2">{meta.step1Caption}</p>
            </WizardStep>

            <WizardStep n={2} title={meta.step2Title}>
              <p>{meta.step2Caption}</p>
            </WizardStep>

            <WizardStep n={3} last title="Paste your Stream key">
              <div className="relative max-w-lg">
                <Input
                  type={showKey ? "text" : "password"}
                  value={streamKey}
                  onChange={e => setStreamKey(e.target.value)}
                  placeholder="Paste your Stream key here"
                  className="pr-9 font-mono text-xs bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                  autoComplete="off"
                />
                <button
                  type="button"
                  onClick={() => setShowKey(v => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  title={showKey ? "Hide key" : "Show key"}
                >
                  {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>

              {warning && (
                <p className="mt-2 text-xs text-amber-400/90 max-w-lg leading-relaxed">{warning}</p>
              )}

              <div className="mt-4 flex items-center gap-3 flex-wrap">
                <Button
                  onClick={handleSave}
                  disabled={upsert.isPending || !streamKey.trim()}
                  style={{ background: GOLD_GRAD }}
                  className="text-gray-900 font-semibold hover:opacity-90 rounded-xl px-6"
                >
                  {upsert.isPending
                    ? "Saving…"
                    : destFor(wizardPlatform)
                      ? "Save new key"
                      : `Connect ${meta.label}`}
                </Button>
                <button onClick={closeWizard} className="text-xs text-gray-500 hover:text-gray-300 transition-colors">
                  Cancel
                </button>
              </div>
              <p className="mt-3 text-xs text-gray-600 flex items-center gap-1.5">
                <Key size={11} /> Your key is encrypted before storage and never shown again.
              </p>
            </WizardStep>
          </div>
        </div>
      )}

      {/* Connected accounts */}
      {destinations.length > 0 && (
        <div>
          <SectionHeading sub="Where your celebration is being streamed.">Connected accounts</SectionHeading>
          <div className="space-y-2">
            {destinations.map(d => {
              const p = (d.platform === "facebook" ? "facebook" : "youtube") as SocialPlatform;
              return (
                <div key={d.id} className="flex items-center gap-3 rounded-2xl border border-gray-700/60 bg-gray-800/60 px-4 py-3">
                  <PlatformIcon platform={p} size={16} />
                  <span className="text-sm font-medium capitalize text-white">{d.platform}</span>
                  <DestinationStatusBadge d={d} />
                  {d.lastError && (
                    <span className="text-xs text-red-400 truncate max-w-[160px] sm:max-w-xs" title={d.lastError}>
                      {d.lastError}
                    </span>
                  )}
                  <div className="ml-auto flex items-center gap-2">
                    <span className="text-[11px] text-gray-500 hidden sm:inline">
                      {d.enabled ? "Streaming on" : "Streaming off"}
                    </span>
                    <Switch
                      checked={d.enabled}
                      disabled={toggle.isPending}
                      onCheckedChange={(next) => toggle.mutate({ did: d.id, platform: p, enabled: next })}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <GoldHairline />

      {/* Reassurance footer */}
      <p className="text-sm text-gray-500 leading-relaxed text-center px-2">
        You only connect once. Set it up a day or two early (YouTube can take 24 hours to enable),
        and we handle the streaming on the day. Need help?{" "}
        <a
          href="https://wa.me/919446776666"
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium hover:opacity-80 transition-opacity"
          style={{ color: GOLD }}
        >
          WhatsApp us
        </a>.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Wishes (guest wall moderation)
// ---------------------------------------------------------------------------

type WishFilter = "all" | "pending" | "approved";

function WishesTab({ stream }: { stream: LiveStream }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [filter, setFilter] = useState<WishFilter>("all");
  const [bulkApproving, setBulkApproving] = useState(false);

  const { data: messages = [], isLoading } = useQuery<GuestMessage[]>({
    queryKey: [`/api/live-manage/${stream.id}/messages/all`],
    queryFn: () => apiRequest(`/api/live-manage/${stream.id}/messages/all`).then(r => r.json()),
    refetchInterval: 15000,
  });

  const approve = useMutation({
    mutationFn: ({ mid, approved }: { mid: number; approved: boolean }) =>
      apiRequest(`/api/live-manage/${stream.id}/messages/${mid}/approve`, { method: "POST", data: { approved } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [`/api/live-manage/${stream.id}/messages/all`] });
    },
    onError: () => toast({ title: "Error", description: "Could not update message", variant: "destructive" }),
  });

  const pending = messages.filter(m => !m.approved);
  const approved = messages.filter(m => m.approved);

  const approveAllPending = async () => {
    if (pending.length === 0) return;
    setBulkApproving(true);
    try {
      for (const m of pending) {
        await approve.mutateAsync({ mid: m.id, approved: true });
      }
      toast({ title: "All approved", description: `${pending.length} wishes are now visible to guests.` });
    } catch {
      toast({ title: "Partly done", description: "Some wishes could not be approved — please retry.", variant: "destructive" });
    } finally {
      setBulkApproving(false);
    }
  };

  if (isLoading) return <p className="text-sm text-gray-500 py-4">Loading wishes…</p>;
  if (messages.length === 0) return (
    <div className="py-12 text-center text-gray-600">
      <Heart size={32} className="mx-auto mb-3 opacity-40" style={{ color: GOLD }} />
      <p className="font-playfair text-lg text-gray-400">No wishes yet</p>
      <p className="text-sm mt-1">Guest messages of love will appear here for your approval.</p>
    </div>
  );

  const visible = filter === "pending" ? pending : filter === "approved" ? approved : messages;

  const pill = (id: WishFilter, label: string, count: number) => (
    <button
      key={id}
      onClick={() => setFilter(id)}
      className={`rounded-full px-3.5 py-1.5 text-xs font-medium border transition-colors ${
        filter === id
          ? "text-gray-900 border-transparent"
          : "bg-gray-800 text-gray-400 border-gray-700/60 hover:border-gray-500 hover:text-gray-200"
      }`}
      style={filter === id ? { background: GOLD_GRAD } : undefined}
    >
      {label} <span className="opacity-70">({count})</span>
    </button>
  );

  return (
    <div className="space-y-5">
      {/* Filter pills + bulk action */}
      <div className="flex flex-wrap items-center gap-2">
        {pill("all", "All", messages.length)}
        {pill("pending", "Pending", pending.length)}
        {pill("approved", "Approved", approved.length)}
        {pending.length > 0 && (
          <Button
            size="sm"
            onClick={approveAllPending}
            disabled={bulkApproving}
            variant="outline"
            className="ml-auto border-gray-600 bg-gray-800 text-gray-200 hover:text-white hover:border-gray-400 rounded-xl"
          >
            {bulkApproving
              ? <><Loader2 size={13} className="mr-1.5 animate-spin" /> Approving…</>
              : <><Check size={13} className="mr-1.5" style={{ color: GOLD }} /> Approve all pending</>}
          </Button>
        )}
      </div>

      {visible.length === 0 ? (
        <p className="text-sm text-gray-600 italic py-6 text-center">
          No {filter === "pending" ? "pending" : "approved"} wishes right now.
        </p>
      ) : (
        <div className="space-y-2.5">
          {visible.map(m => (
            <div
              key={m.id}
              className="rounded-2xl border border-gray-700/60 bg-gray-800/50 px-4 py-3.5 sm:px-5 transition-colors hover:bg-gray-800/80"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium text-white">{m.name || "Anonymous"}</p>
                    <span className="text-[11px] text-gray-600">
                      {new Date(m.createdAt).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
                    </span>
                    {!m.approved && (
                      <span className="rounded-full bg-amber-900/50 border border-amber-700/50 px-1.5 py-0.5 text-[10px] font-medium text-amber-300">
                        Awaiting
                      </span>
                    )}
                  </div>
                  <p className="mt-1.5 text-sm text-gray-300 leading-relaxed break-words">{m.message}</p>
                </div>
                <div className="flex-shrink-0 flex items-center gap-2">
                  {m.approved ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => approve.mutate({ mid: m.id, approved: false })}
                      disabled={approve.isPending || bulkApproving}
                      className="text-gray-500 hover:text-red-400 hover:bg-gray-700/50 rounded-lg"
                    >
                      <EyeOff size={13} className="mr-1.5" /> Hide
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => approve.mutate({ mid: m.id, approved: true })}
                      disabled={approve.isPending || bulkApproving}
                      className="text-gray-900 font-semibold hover:opacity-90 rounded-lg"
                      style={{ background: GOLD_GRAD }}
                    >
                      <Check size={13} className="mr-1.5" /> Approve
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Keepsake — one-time payment to preserve the wedding film beyond the free window
// ---------------------------------------------------------------------------

interface KeepsakePlan {
  id: string;
  months: number;
  amount: number;       // in paise
  price: string;        // display, e.g. "₹499"
  label: string;        // display, e.g. "6 months"
}

interface KeepsakeInfo {
  expiresAt: string | null;
  renewedUntil: string | null;
  effectiveExpiry: string | null;
  daysLeft: number | null;
  state: "free" | "expiring" | "expired" | "none";
  coupleNames: string | null;
  plans: KeepsakePlan[];
}

/** "9 Jul 2026" style short date for the keepsake hero line. */
function formatShortDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/** Load the Razorpay checkout script once, resolving when window.Razorpay is ready. */
let razorpayScriptPromise: Promise<void> | null = null;
function ensureRazorpay(): Promise<void> {
  if (typeof window !== "undefined" && (window as any).Razorpay) return Promise.resolve();
  if (razorpayScriptPromise) return razorpayScriptPromise;
  razorpayScriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[src="https://checkout.razorpay.com/v1/checkout.js"]'
    );
    if (existing) {
      if ((window as any).Razorpay) { resolve(); return; }
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => { razorpayScriptPromise = null; reject(new Error("Failed to load Razorpay")); });
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => { razorpayScriptPromise = null; reject(new Error("Failed to load Razorpay")); };
    document.body.appendChild(script);
  });
  return razorpayScriptPromise;
}

function KeepsakeStatusBadge({ info }: { info: KeepsakeInfo }) {
  if (info.state === "free") {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium"
        style={{ background: `${GOLD}1a`, color: GOLD, boxShadow: `inset 0 0 0 1px ${GOLD}55` }}
      >
        <ShieldCheck size={12} /> Free · included with your booking
      </span>
    );
  }
  if (info.state === "expiring") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-700/50 bg-amber-900/40 px-3 py-1 text-xs font-medium text-amber-300">
        <Clock size={12} /> Expires in {info.daysLeft ?? 0} days
      </span>
    );
  }
  if (info.state === "expired") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-red-800/60 bg-red-950/50 px-3 py-1 text-xs font-medium text-red-300">
        <Clock size={12} /> Expired — renew to restore
      </span>
    );
  }
  return null;
}

function KeepsakeTab({ stream }: { stream: LiveStream }) {
  const { toast } = useToast();

  const { data: info, isLoading, refetch } = useQuery<KeepsakeInfo>({
    queryKey: [`/api/live-manage/${stream.id}/keepsake`],
    queryFn: () => apiRequest(`/api/live-manage/${stream.id}/keepsake`).then(r => r.json()),
  });

  // Which plan (by id) currently has a payment in flight — disables all buttons.
  const [payingPlan, setPayingPlan] = useState<string | null>(null);

  const startCheckout = async (plan: KeepsakePlan) => {
    if (payingPlan) return;
    setPayingPlan(plan.id);
    try {
      // 1. Create the order server-side
      const orderRes = await apiRequest(`/api/live-manage/${stream.id}/keepsake/order`, {
        method: "POST",
        data: { plan: plan.id },
      });
      if (!orderRes.ok) {
        const body = await orderRes.json().catch(() => ({}));
        throw new Error(body?.error || "Could not start the payment. Please try again.");
      }
      const order = await orderRes.json() as {
        orderId: string; amount: number; currency: string;
        keyId: string; name: string; description: string;
      };

      // 2. Make sure the Razorpay checkout script is loaded
      await ensureRazorpay();
      const RazorpayCtor = (window as any).Razorpay;
      if (!RazorpayCtor) throw new Error("Payment window failed to load. Please try again.");

      // 3. Open checkout
      const rzp = new RazorpayCtor({
        key: order.keyId,
        order_id: order.orderId,
        amount: order.amount,
        currency: order.currency,
        name: order.name,
        description: order.description,
        theme: { color: GOLD },
        prefill: { name: info?.coupleNames || "" },
        modal: {
          ondismiss: () => setPayingPlan(null),
        },
        handler: async (resp: {
          razorpay_order_id: string;
          razorpay_payment_id: string;
          razorpay_signature: string;
        }) => {
          try {
            const verifyRes = await apiRequest(`/api/live-manage/${stream.id}/keepsake/verify`, {
              method: "POST",
              data: {
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
                plan: plan.id,
              },
            });
            const verifyBody = await verifyRes.json().catch(() => ({}));
            // A verify that the webhook already reconciled answers { success, alreadyProcessed }
            // with no `ok` — that is a SUCCESS, never "verification failed… refunded" (audit L7).
            if (verifyRes.ok && keepsakeVerified(verifyBody)) {
              toast({
                title: "Your film is preserved 💛",
                description: `Kept for a further ${plan.label}. Thank you.`,
              });
              await refetch();
            } else {
              toast({
                title: "Payment verification failed",
                description: verifyBody?.error || "If money was deducted, it will be refunded. Please contact us.",
                variant: "destructive",
              });
            }
          } catch (err: any) {
            toast({
              title: "Verification error",
              description: err?.message ?? "Please contact LA Mirage to confirm your payment.",
              variant: "destructive",
            });
          } finally {
            setPayingPlan(null);
          }
        },
      });
      rzp.on?.("payment.failed", (resp: any) => {
        toast({
          title: "Payment failed",
          description: resp?.error?.description || "The payment could not be completed. Please try again.",
          variant: "destructive",
        });
        setPayingPlan(null);
      });
      rzp.open();
    } catch (err: any) {
      toast({
        title: "Couldn't start payment",
        description: err?.message ?? "Please try again in a moment.",
        variant: "destructive",
      });
      setPayingPlan(null);
    }
  };

  if (isLoading) {
    return (
      <div className="py-12 text-center text-gray-500">
        <Loader2 size={24} className="mx-auto mb-3 animate-spin" style={{ color: GOLD }} />
        <p className="text-sm">Loading your keepsake details…</p>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="py-12 text-center text-gray-600">
        <Gift size={30} className="mx-auto mb-3 opacity-40" style={{ color: GOLD }} />
        <p className="text-sm">We couldn't load your keepsake details. Please refresh.</p>
      </div>
    );
  }

  const heroDate = formatShortDate(info.effectiveExpiry);

  return (
    <div className="space-y-8">
      {/* Hero */}
      <div
        className="relative overflow-hidden rounded-2xl border border-gray-700/60 px-5 py-7 sm:px-8 sm:py-8"
        style={{ background: `radial-gradient(120% 140% at 15% 0%, #2a2415 0%, #14120c 45%, #0b0b0d 100%)` }}
      >
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />
        <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-gray-400 mb-3">
          <Gift size={13} style={{ color: GOLD }} />
          Your wedding film
        </p>
        {info.state === "none" || !heroDate ? (
          <h3 className="font-playfair text-2xl sm:text-3xl text-white leading-snug">
            Your wedding film is safely preserved.
          </h3>
        ) : (
          <h3 className="font-playfair text-2xl sm:text-3xl text-white leading-snug">
            Your wedding film is preserved until{" "}
            <span style={{ color: GOLD }}>{heroDate}</span>
          </h3>
        )}
        <div className="mt-4">
          <KeepsakeStatusBadge info={info} />
        </div>
      </div>

      {/* Explanatory copy */}
      <div className="rounded-2xl border border-gray-700/60 bg-gray-800/40 px-5 py-4 flex items-start gap-3">
        <Sparkles size={16} className="mt-0.5 flex-shrink-0" style={{ color: GOLD }} />
        <p className="text-sm text-gray-300 leading-relaxed">
          Every LA Mirage Live film is kept free until the date shown above. Keep it beyond that with a
          one-time payment — no subscription, cancel nothing. <b className="text-white">This
          renews your wedding photos too</b> — one keepsake covers both your film and your photos.
        </p>
      </div>

      {/* Plan cards */}
      <div>
        <SectionHeading sub="A single payment adds time to your film — choose how long.">
          Keep your film longer
        </SectionHeading>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {info.plans.map(plan => {
            const busy = payingPlan === plan.id;
            const disabled = payingPlan !== null;
            return (
              <div
                key={plan.id}
                className="relative rounded-2xl border border-gray-700/60 bg-gray-800/60 p-5 flex flex-col overflow-hidden"
              >
                <div className="absolute top-0 left-0 right-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />
                <div className="flex items-baseline gap-2">
                  <span className="font-playfair text-3xl leading-none" style={{ color: GOLD }}>
                    {plan.price}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.16em] text-gray-500">one-time</span>
                </div>
                <p className="mt-2 text-sm text-gray-300">
                  Adds <span className="text-white font-medium">{plan.label}</span> to your film.
                </p>
                <Button
                  onClick={() => startCheckout(plan)}
                  disabled={disabled}
                  className="mt-5 w-full rounded-xl text-gray-900 font-semibold hover:opacity-90 disabled:opacity-50"
                  style={{ background: GOLD_GRAD }}
                >
                  {busy
                    ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Opening…</>
                    : <>Keep for {plan.label}</>}
                </Button>
              </div>
            );
          })}
        </div>
        <p className="mt-4 text-xs text-gray-600 flex items-center gap-1.5">
          <ShieldCheck size={12} /> Secure one-time payment via Razorpay — no recurring charges.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main portal page
// ---------------------------------------------------------------------------


// lm-studio-tab: scoped hook for the index.css override that beats the global
// `button[role="tab"] { color: white !important }` rule (active tab is gold-bg,
// so its label must be dark — see .lm-studio-tab rules in index.css).
const TAB_TRIGGER_CLASS =
  "lm-studio-tab flex items-center gap-1.5 whitespace-nowrap rounded-lg px-3.5 data-[state=active]:bg-[#C5A572] data-[state=active]:text-gray-900 data-[state=active]:font-semibold";

// ---------------------------------------------------------------------------
// Download tab — on-demand MP4 export of the finished ceremony (1080p / 4K).
// ---------------------------------------------------------------------------

function DownloadOption({ stream, quality, label, note }: { stream: LiveStream; quality: string; label: string; note: string }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  const { data, refetch } = useQuery<any>({
    queryKey: [`/api/live-manage/${stream.id}/download`, quality],
    queryFn: () => apiRequest(`/api/live-manage/${stream.id}/download?quality=${quality}`).then(r => r.json()),
    refetchInterval: (q: any) => (q?.state?.data?.status === "generating" ? 5000 : false),
  });
  const status: string = data?.status ?? "idle";
  const sizeMb = data?.sizeBytes ? Math.round(data.sizeBytes / 1048576) : null;

  const prepare = async () => {
    setBusy(true);
    try {
      const r = await apiRequest(`/api/live-manage/${stream.id}/download`, { method: "POST", data: { quality } });
      if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b?.error || "Could not start"); }
      refetch();
    } catch (e: any) { toast({ title: "Couldn't start", description: e.message, variant: "destructive" }); }
    finally { setBusy(false); }
  };

  const download = async () => {
    setBusy(true);
    try {
      const r = await apiRequest(`/api/live-manage/${stream.id}/download-token?quality=${quality}`);
      if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b?.error || "Not ready"); }
      const { url } = await r.json();
      window.open(url, "_blank");
    } catch (e: any) { toast({ title: "Download failed", description: e.message, variant: "destructive" }); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-gray-700/60 bg-gray-800/40 p-4 flex items-center justify-between gap-4">
      <div className="min-w-0">
        <p className="text-sm font-medium text-white">{label}</p>
        <p className="text-xs text-gray-400 mt-0.5">{note}{sizeMb ? ` · ${sizeMb} MB` : ""}</p>
        {status === "error" && (data?.message || data?.error) && <p className="text-xs text-red-400 mt-1">{data.message || data.error}</p>}
        {status === "generating" && data?.message && <p className="text-xs text-amber-300/90 mt-1" aria-live="polite">{data.message}</p>}
      </div>
      {status === "ready" ? (
        <Button onClick={download} disabled={busy} className="shrink-0 px-4 py-2 text-sm font-semibold text-gray-900 rounded-xl hover:opacity-90" style={{ background: GOLD_GRAD }}>
          <Download size={14} className="mr-1.5" /> Download
        </Button>
      ) : status === "generating" ? (
        <span className="shrink-0 text-xs text-amber-400 flex items-center gap-1.5"><Loader2 size={13} className="animate-spin" /> {data?.phase === "queued" ? "Queued" : Number.isFinite(data?.progress) ? `${data.progress}%` : "Preparing…"}</span>
      ) : status === "error" ? (
        <Button onClick={prepare} disabled={busy} variant="outline" className="shrink-0 text-sm border-gray-600 text-gray-300">Retry</Button>
      ) : (
        <Button onClick={prepare} disabled={busy} className="shrink-0 px-4 py-2 text-sm font-semibold text-gray-900 rounded-xl hover:opacity-90" style={{ background: GOLD_GRAD }}>
          {busy ? "Starting…" : "Prepare download"}
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Web address tab — the couple's public link. Two parts:
//   1. Their free lamirage.live subdomain (rename anytime)
//   2. An optional paid custom domain (₹999, fully automated via Name.com + Cloudflare)
// ---------------------------------------------------------------------------

type DomainInfo = {
  customDomain: string | null;
  tld: string | null;
  type: string | null;
  status: null | "payment_pending" | "purchasing" | "dns_pending" | "active" | "failed";
  requestedAt: string | null;
  activatedAt: string | null;
};
type DomainOption = { domain: string; tld: string; available: boolean; price: number };

function WebAddressTab({ stream }: { stream: LiveStream }) {
  return (
    <div className="space-y-6">
      <SubdomainCard stream={stream} />
      <CustomDomainCard stream={stream} />
    </div>
  );
}

// --- Free subdomain editor (john-jane.lamirage.live) --------------------------
function SubdomainCard({ stream }: { stream: LiveStream }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [value, setValue] = useState(stream.subdomain ?? "");
  const avail = useSubdomainAvailability(value, stream.subdomain);
  const trimmed = value.trim().toLowerCase();
  const changed = trimmed !== (stream.subdomain ?? "");
  const canSave = changed && trimmed.length >= 3 && avail !== "taken" && avail !== "invalid" && avail !== "checking";

  const save = useMutation({
    mutationFn: () =>
      apiRequest(`/api/live-manage/${stream.id}`, { method: "PATCH", data: { subdomain: trimmed } }).then(async r => {
        if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b?.error || "Could not save"); }
        return r.json();
      }),
    onSuccess: () => {
      toast({ title: "Web address updated", description: `${trimmed}.lamirage.live` });
      qc.invalidateQueries({ queryKey: ["/api/live-manage"] });
    },
    onError: (e: any) => toast({ title: "Couldn't save", description: e?.message ?? "Please try another name.", variant: "destructive" }),
  });

  return (
    <div className="rounded-xl border border-gray-700/60 bg-gray-800/40 p-5">
      <div className="flex items-center gap-2 mb-1">
        <Globe size={16} style={{ color: GOLD }} />
        <h3 className="text-base font-semibold text-white">Your live link</h3>
      </div>
      <p className="text-sm text-gray-400 mb-4">This is the free web address guests use to watch. You can change it anytime before the wedding.</p>

      <label className="text-xs font-medium text-gray-400">Web address</label>
      <div className="mt-1 flex items-stretch rounded-lg border border-gray-600 bg-gray-900 overflow-hidden focus-within:border-[color:var(--gold)]" style={{ ["--gold" as any]: GOLD }}>
        <input
          value={value}
          onChange={e => setValue(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
          placeholder="john-jane"
          className="flex-1 min-w-0 bg-transparent px-3 py-2 text-sm text-white outline-none"
          maxLength={40}
        />
        <span className="flex items-center px-3 text-sm text-gray-500 bg-gray-800/70 border-l border-gray-700 whitespace-nowrap">.lamirage.live</span>
      </div>
      <div className="mt-1.5 h-4 text-xs">
        {avail === "checking" && <span className="text-gray-500 flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> Checking…</span>}
        {avail === "available" && changed && <span className="text-emerald-400 flex items-center gap-1"><Check size={11} /> Available</span>}
        {avail === "taken" && <span className="text-red-400">That address is taken — try another.</span>}
        {avail === "invalid" && <span className="text-red-400">Use 3–40 lowercase letters, numbers and hyphens.</span>}
        {avail === "idle" && !changed && value && <span className="text-gray-500">This is your current link</span>}
      </div>

      <button
        onClick={() => save.mutate()}
        disabled={!canSave || save.isPending}
        className="mt-3 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-gray-900 disabled:opacity-40"
        style={{ background: GOLD_GRAD }}
      >
        {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save address
      </button>
    </div>
  );
}

// --- Paid custom domain (yourwedding.site etc.) -------------------------------
function CustomDomainCard({ stream }: { stream: LiveStream }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [base, setBase] = useState("");
  const [options, setOptions] = useState<DomainOption[] | null>(null);
  const [checking, setChecking] = useState(false);
  const [buying, setBuying] = useState<string | null>(null);

  const { data: info, refetch } = useQuery<DomainInfo>({
    queryKey: [`/api/live-manage/${stream.id}/domain`],
    queryFn: () => apiRequest(`/api/live-manage/${stream.id}/domain`).then(r => r.json()),
    // Poll while provisioning so the couple sees it flip to Active without reloading.
    refetchInterval: (q) => {
      const s = (q.state.data as DomainInfo | undefined)?.status;
      return s === "purchasing" || s === "dns_pending" ? 15000 : false;
    },
  });

  const status = info?.status ?? null;
  const hasActiveOrPending = status === "active" || status === "dns_pending" || status === "purchasing";

  const checkAvailability = async () => {
    const b = base.trim().toLowerCase().replace(/[^a-z0-9-]/g, "");
    if (b.length < 3) { toast({ title: "Enter a name", description: "At least 3 characters.", variant: "destructive" }); return; }
    setChecking(true); setOptions(null);
    try {
      const r = await apiRequest(`/api/live-manage/${stream.id}/domain-availability?base=${encodeURIComponent(b)}`);
      const body = await r.json();
      setOptions(body.domains || []);
    } catch {
      toast({ title: "Couldn't check availability", description: "Please try again.", variant: "destructive" });
    } finally { setChecking(false); }
  };

  const refresh = useMutation({
    mutationFn: () => apiRequest(`/api/live-manage/${stream.id}/domain/refresh`, { method: "POST" }).then(r => r.json()),
    onSuccess: () => { refetch(); qc.invalidateQueries({ queryKey: ["/api/live-manage"] }); },
  });

  const buy = async (domain: string) => {
    setBuying(domain);
    try {
      const orderRes = await apiRequest(`/api/live-manage/${stream.id}/domain/order`, { method: "POST", data: { domain } });
      if (!orderRes.ok) { const b = await orderRes.json().catch(() => ({})); throw new Error(b?.error || "Could not start the purchase."); }
      const order = await orderRes.json() as { orderId: string; amount: number; currency: string; keyId: string; domain: string };
      await ensureRazorpay();
      const RazorpayCtor = (window as any).Razorpay;
      if (!RazorpayCtor) throw new Error("Payment window failed to load.");
      const rzp = new RazorpayCtor({
        key: order.keyId,
        order_id: order.orderId,
        amount: order.amount,
        currency: order.currency,
        name: "LA Mirage Live",
        description: `Custom domain — ${order.domain}`,
        theme: { color: GOLD },
        modal: { ondismiss: () => setBuying(null) },
        handler: async (resp: any) => {
          try {
            const vRes = await apiRequest(`/api/live-manage/${stream.id}/domain/verify`, {
              method: "POST",
              data: { razorpay_order_id: resp.razorpay_order_id, razorpay_payment_id: resp.razorpay_payment_id, razorpay_signature: resp.razorpay_signature },
            });
            const v = await vRes.json().catch(() => ({}));
            if (vRes.ok) {
              toast({ title: "Domain secured 🎉", description: `We're setting up ${order.domain}. It'll be live within ~15 minutes.` });
              setOptions(null); setBase("");
              refetch();
            } else {
              toast({ title: "Verification failed", description: v?.error || "If money was deducted it will be refunded. Please contact us.", variant: "destructive" });
            }
          } catch (err: any) {
            toast({ title: "Verification error", description: err?.message ?? "Please contact LA Mirage.", variant: "destructive" });
          } finally { setBuying(null); }
        },
      });
      rzp.on?.("payment.failed", (r: any) => { toast({ title: "Payment failed", description: r?.error?.description || "Please try again.", variant: "destructive" }); setBuying(null); });
      rzp.open();
    } catch (err: any) {
      toast({ title: "Couldn't start purchase", description: err?.message ?? "Please try again.", variant: "destructive" });
      setBuying(null);
    }
  };

  return (
    <div className="rounded-xl border border-gray-700/60 bg-gray-800/40 p-5">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles size={16} style={{ color: GOLD }} />
        <h3 className="text-base font-semibold text-white">Your own domain <span className="text-xs font-normal text-gray-500">— optional</span></h3>
      </div>
      <p className="text-sm text-gray-400 mb-4">
        Want a personal web address like <span className="text-gray-300">yourwedding.site</span> instead? We'll buy it, secure it with HTTPS and point it at your live page — all automatic. One-time <strong className="text-white">₹999</strong>.
      </p>

      {/* Current custom-domain state */}
      {status === "active" && info?.customDomain && (
        <div className="rounded-lg border border-emerald-700/50 bg-emerald-900/20 p-4">
          <div className="flex items-center gap-2 text-emerald-300 text-sm font-semibold"><ShieldCheck size={15} /> Live &amp; secured</div>
          <a href={`https://${info.customDomain}`} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1.5 text-white font-medium hover:underline">
            {info.customDomain} <ExternalLink size={13} />
          </a>
          <p className="text-xs text-gray-400 mt-1">Guests can now watch at your own address. Your .lamirage.live link keeps working too.</p>
        </div>
      )}

      {(status === "purchasing" || status === "dns_pending") && info?.customDomain && (
        <div className="rounded-lg border border-amber-700/50 bg-amber-900/20 p-4">
          <div className="flex items-center gap-2 text-amber-300 text-sm font-semibold">
            <Loader2 size={15} className="animate-spin" /> Setting up {info.customDomain}
          </div>
          <p className="text-xs text-gray-400 mt-1">
            {status === "purchasing"
              ? "Registering your domain…"
              : "Securing HTTPS and pointing it to your live page. This usually takes 5–15 minutes."}
          </p>
          <button
            onClick={() => refresh.mutate()}
            disabled={refresh.isPending}
            className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-amber-600/50 px-3 py-1.5 text-xs font-medium text-amber-200 hover:bg-amber-900/30 disabled:opacity-40"
          >
            {refresh.isPending ? <Loader2 size={12} className="animate-spin" /> : <Radio size={12} />} Check status
          </button>
        </div>
      )}

      {status === "failed" && (
        <div className="rounded-lg border border-red-700/50 bg-red-900/20 p-4 mb-4">
          <div className="flex items-center gap-2 text-red-300 text-sm font-semibold"><X size={15} /> Setup didn't complete</div>
          <p className="text-xs text-gray-400 mt-1">Something went wrong while setting up {info?.customDomain}. Our team has been alerted — please contact us and we'll sort it out (you won't be charged twice).</p>
        </div>
      )}

      {/* Purchase flow — only when nothing is active/pending */}
      {!hasActiveOrPending && (
        <div className={status === "failed" ? "" : ""}>
          <label className="text-xs font-medium text-gray-400">Search a name</label>
          <div className="mt-1 flex gap-2">
            <input
              value={base}
              onChange={e => { setBase(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "")); setOptions(null); }}
              onKeyDown={e => { if (e.key === "Enter") checkAvailability(); }}
              placeholder="johnandjane"
              className="flex-1 min-w-0 rounded-lg border border-gray-600 bg-gray-900 px-3 py-2 text-sm text-white outline-none focus:border-[color:var(--gold)]"
              style={{ ["--gold" as any]: GOLD }}
              maxLength={40}
            />
            <button
              onClick={checkAvailability}
              disabled={checking || base.trim().length < 3}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold text-gray-900 disabled:opacity-40"
              style={{ background: GOLD_GRAD }}
            >
              {checking ? <Loader2 size={14} className="animate-spin" /> : <Globe size={14} />} Search
            </button>
          </div>

          {options && (
            <div className="mt-3 space-y-2">
              {options.every(o => !o.available) && (
                <p className="text-xs text-gray-500">None of these are free for "{base}". Try a different name.</p>
              )}
              {options.map(o => (
                <div key={o.domain} className={`flex items-center justify-between rounded-lg border px-3 py-2.5 ${o.available ? "border-gray-600 bg-gray-900/60" : "border-gray-800 bg-gray-900/30 opacity-50"}`}>
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-white truncate">{o.domain}</span>
                    {!o.available && <span className="ml-2 text-xs text-gray-500">taken</span>}
                  </div>
                  {o.available && (
                    <button
                      onClick={() => buy(o.domain)}
                      disabled={!!buying}
                      className="shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-900 disabled:opacity-40"
                      style={{ background: GOLD_GRAD }}
                    >
                      {buying === o.domain ? <Loader2 size={12} className="animate-spin" /> : null} Buy ₹999
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          <p className="mt-3 text-xs text-gray-500 leading-relaxed">
            The domain is registered for you for 1 year and stays pointed at your live page and replay. No DNS, certificates or setup on your end.
          </p>
        </div>
      )}
    </div>
  );
}

function DownloadTab({ stream }: { stream: LiveStream }) {
  if (stream.status !== "REPLAY") {
    return (
      <div className="rounded-xl border border-gray-700/60 bg-gray-800/40 p-8 text-center">
        <Clock size={24} className="mx-auto mb-3 text-gray-500" />
        <p className="text-sm text-gray-300">Your video download will be ready shortly after your live ends.</p>
        <p className="text-xs text-gray-500 mt-1">Come back to this tab once the ceremony is over.</p>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-white">Download your video</h3>
        <p className="text-sm text-gray-400 mt-0.5">Save a copy of your ceremony. Your files are prepared for you after the ceremony; if one is not ready yet, press Prepare — a long 4K event can take an hour or more, and you can leave this page meanwhile.</p>
      </div>
      <DownloadOption stream={stream} quality="hd" label="Full HD — 1080p" note="Best for keeping & sharing — downloads reliably" />
      <DownloadOption stream={stream} quality="uhd" label="Ultra HD — 4K" note="Maximum quality — a very large file, best on a fast connection" />
      <div className="rounded-xl border border-amber-800/40 bg-amber-900/20 px-4 py-3">
        <p className="text-xs text-amber-300/90 leading-relaxed flex items-start gap-2">
          <Gift size={14} className="shrink-0 mt-0.5" />
          <span><strong>Keep your film:</strong> your video stays available to stream and download{(stream.renewedUntil || stream.expiresAt) ? <> until <strong>{new Date((stream.renewedUntil || stream.expiresAt) as string).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", day: "numeric", month: "long", year: "numeric" })}</strong></> : null}. To extend it, renew from the <strong>Keepsake</strong> tab.</span>
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// First-visit setup wizard — subdomain (required) → names → cover+welcome →
// theme → privacy. Everything but the subdomain is skippable. The stream already
// exists with a sensible default subdomain, so this just lets them personalize.
// ---------------------------------------------------------------------------

function SetupWizard({ stream, onDone }: { stream: LiveStream; onDone?: () => void }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [step, setStep] = useState(0);
  const isEditing = !!onDone; // re-run from the studio (already set up)

  const [form, setForm] = useState({
    subdomain: stream.subdomain ?? "",
    coupleNames: stream.coupleNamesOverride ?? "",
    coverImageKey: stream.coverImageKey ?? "",
    welcomeMessage: stream.welcomeMessage ?? "",
    theme: stream.theme ?? "midnight",
    isPublic: stream.isPublic ?? true,
    guestPassword: "",
  });
  const set = (p: Partial<typeof form>) => setForm(f => ({ ...f, ...p }));

  const availState = useSubdomainAvailability(form.subdomain, stream.subdomain);
  const subOk = form.subdomain.trim().length >= 3 &&
    (availState === "available" || availState === "idle"); // idle = unchanged from the valid default

  const finish = useMutation({
    mutationFn: (body: any) =>
      apiRequest(`/api/live-manage/${stream.id}`, { method: "PATCH", data: body }).then(async r => {
        if (!r.ok) { const b = await r.json().catch(() => ({})); throw Object.assign(new Error(b?.error || "failed"), { status: r.status }); }
        return r.json();
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/live-manage"] });
      if (isEditing) toast({ title: "Saved", description: "Your live stream details are updated." });
      onDone?.();
    },
    onError: (e: any) => {
      const status = e?.status;
      toast({
        title: status === 409 ? "Subdomain taken" : "Couldn't save",
        description: status === 409 ? "That link is taken — pick another on step 1." : (e?.message ?? "Please try again."),
        variant: "destructive",
      });
      if (status === 409 || status === 400) setStep(0);
    },
  });

  const complete = () => {
    const body: any = { completeSetup: true };
    if (form.subdomain.trim() && form.subdomain.trim() !== (stream.subdomain ?? "")) body.subdomain = form.subdomain.trim().toLowerCase();
    if (form.coupleNames.trim() && form.coupleNames.trim() !== (stream.coupleNamesOverride ?? "")) body.coupleNamesOverride = form.coupleNames.trim();
    if (form.coverImageKey && form.coverImageKey !== (stream.coverImageKey ?? "")) body.coverImageKey = form.coverImageKey;
    if (form.welcomeMessage.trim() && form.welcomeMessage !== (stream.welcomeMessage ?? "")) body.welcomeMessage = form.welcomeMessage;
    if (form.theme && form.theme !== (stream.theme ?? "midnight")) body.theme = form.theme;
    if (form.isPublic !== (stream.isPublic ?? true)) body.isPublic = form.isPublic;
    if (form.guestPassword.trim().length >= 6) body.guestPassword = form.guestPassword.trim();
    finish.mutate(body);
  };

  const STEPS = ["Your link", "Names", "Welcome", "Theme", "Privacy"];
  const isLast = step === STEPS.length - 1;
  const next = () => (isLast ? complete() : setStep(s => s + 1));

  const previewUrl = `${(form.subdomain.trim() || stream.subdomain || "your-wedding").toLowerCase()}.lamirage.live`;

  return (
    <div className="max-w-xl mx-auto px-4 py-8 sm:py-12">
      <div className="text-center mb-6">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border"
          style={{ borderColor: `${GOLD}66`, background: `${GOLD}11` }}>
          <Sparkles size={26} style={{ color: GOLD }} />
        </div>
        <h1 className="font-playfair text-2xl text-white">{isEditing ? "Edit your live stream" : "Let's set up your live stream"}</h1>
        <p className="text-sm text-gray-400 mt-1">{isEditing ? "Update any of your live stream details." : "A quick one-time setup — it's already enabled, this just makes it yours."}</p>
      </div>

      {/* progress dots */}
      <div className="flex items-center justify-center gap-1.5 mb-6">
        {STEPS.map((_, i) => (
          <div key={i} className="h-1.5 rounded-full transition-all"
            style={{ width: i === step ? 24 : 8, background: i <= step ? GOLD : "rgba(255,255,255,0.15)" }} />
        ))}
      </div>

      <Card className="bg-gray-900 border-gray-700/60 rounded-2xl">
        <CardContent className="p-5 sm:p-6 min-h-[280px] flex flex-col">
          <p className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">Step {step + 1} of {STEPS.length}</p>

          {/* STEP 1 — subdomain (required) */}
          {step === 0 && (
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2"><Globe size={18} style={{ color: GOLD }} /> Your shareable link</h2>
              <p className="text-sm text-gray-400 mt-1 mb-4">This is the web address you'll share with guests. Keep the suggestion or make it your own.</p>
              <div className="flex items-stretch rounded-xl border border-gray-700 overflow-hidden bg-gray-800/60">
                <input
                  value={form.subdomain}
                  onChange={e => set({ subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                  placeholder="john-jane-aug23"
                  maxLength={40}
                  className="flex-1 bg-transparent px-3.5 py-3 text-white text-sm outline-none"
                />
                <span className="flex items-center px-3 text-sm text-gray-500 bg-gray-800/80 border-l border-gray-700">.lamirage.live</span>
              </div>
              <div className="mt-2 text-xs h-5">
                {availState === "checking" && <span className="text-gray-500">Checking availability…</span>}
                {availState === "available" && <span className="text-emerald-400 flex items-center gap-1"><Check size={12} /> Available</span>}
                {availState === "taken" && <span className="text-rose-400">Taken — try another</span>}
                {availState === "invalid" && <span className="text-rose-400">Use 3–40 lowercase letters, numbers, hyphens</span>}
                {availState === "idle" && form.subdomain && <span className="text-gray-500">This is your current link</span>}
              </div>
              <div className="mt-3 rounded-lg bg-black/30 border border-gray-800 px-3 py-2 text-xs text-gray-400">
                Guests will watch at <span className="text-amber-300 font-mono">{previewUrl}</span>
              </div>
            </div>
          )}

          {/* STEP 2 — couple names */}
          {step === 1 && (
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2"><Heart size={18} style={{ color: GOLD }} /> Couple names</h2>
              <p className="text-sm text-gray-400 mt-1 mb-4">Shown on your watch page. We pre-filled it from your booking.</p>
              <input
                value={form.coupleNames}
                onChange={e => set({ coupleNames: e.target.value })}
                placeholder="John & Jane"
                maxLength={200}
                className="w-full rounded-xl border border-gray-700 bg-gray-800/60 px-3.5 py-3 text-white text-sm outline-none focus:border-gray-500"
              />
            </div>
          )}

          {/* STEP 3 — cover + welcome */}
          {step === 2 && (
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2"><ImageIcon size={18} style={{ color: GOLD }} /> Welcome your guests</h2>
              <p className="text-sm text-gray-400 mt-1 mb-4">A cover photo and a short note shown before you go live. Optional.</p>
              <CoverImageUploader streamId={stream.id} value={form.coverImageKey} onChange={url => set({ coverImageKey: url })} />
              <textarea
                value={form.welcomeMessage}
                onChange={e => set({ welcomeMessage: e.target.value })}
                placeholder="So happy to have you here — the ceremony begins soon 💛"
                maxLength={2000}
                rows={3}
                className="mt-3 w-full rounded-xl border border-gray-700 bg-gray-800/60 px-3.5 py-3 text-white text-sm outline-none focus:border-gray-500 resize-none"
              />
            </div>
          )}

          {/* STEP 4 — theme */}
          {step === 3 && (
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2"><Sparkles size={18} style={{ color: GOLD }} /> Watch page theme</h2>
              <p className="text-sm text-gray-400 mt-1 mb-4">Set the mood for your guests.</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {LIVE_THEMES.map((t: any) => (
                  <button key={t.id} type="button" onClick={() => set({ theme: t.id })}
                    className="relative rounded-2xl border p-3.5 text-left transition-all"
                    style={form.theme === t.id ? { borderColor: GOLD, boxShadow: `0 0 0 1px ${GOLD}` } : { borderColor: "rgba(255,255,255,0.12)" }}>
                    <div className="flex gap-1.5 mb-2.5">
                      <div className="h-6 w-6 rounded-full" style={{ background: t.swatch[0], border: "1px solid rgba(255,255,255,0.12)" }} />
                      <div className="h-6 w-6 rounded-full -ml-2.5" style={{ background: t.swatch[1], border: "1px solid rgba(0,0,0,0.25)" }} />
                    </div>
                    <p className="text-xs font-medium text-gray-300">{t.name}</p>
                    {form.theme === t.id && (
                      <div className="absolute top-2.5 right-2.5 flex h-4 w-4 items-center justify-center rounded-full" style={{ background: GOLD }}>
                        <Check size={10} className="text-gray-900" strokeWidth={3.5} />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* STEP 5 — visibility + optional password */}
          {step === 4 && (
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2"><ShieldCheck size={18} style={{ color: GOLD }} /> Visibility &amp; privacy</h2>
              <p className="text-sm text-gray-400 mt-1 mb-4">Your stream is enabled by default. Choose how it's found and whether guests need a password.</p>

              {/* Show on LA Mirage Live page (isPublic) — ON by default */}
              <button type="button" onClick={() => set({ isPublic: !form.isPublic })}
                className="w-full text-left rounded-xl border border-gray-700 bg-gray-800/40 p-3.5 flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-white flex items-center gap-2"><Globe size={15} /> Show on LA Mirage Live page</p>
                  <p className="text-xs text-gray-400 mt-0.5">Feature your wedding in the public "what's live now" list at lamirage.live/live.</p>
                </div>
                <span className="relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors"
                  style={{ background: form.isPublic ? GOLD : "rgba(255,255,255,0.15)" }}>
                  <span className="absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all" style={{ left: form.isPublic ? 22 : 2 }} />
                </span>
              </button>

              {/* Optional guest password */}
              <div className="mt-3">
                <Label className="text-sm text-gray-400 mb-1.5 block flex items-center gap-1.5"><Key size={13} /> Guest password <span className="text-gray-600">(optional)</span></Label>
                <input
                  type="text"
                  value={form.guestPassword}
                  onChange={e => set({ guestPassword: e.target.value })}
                  placeholder="Leave blank for open access · min 6 characters to set"
                  className="w-full rounded-xl border border-gray-700 bg-gray-800/60 px-3.5 py-2.5 text-white text-sm outline-none focus:border-gray-500"
                />
                <p className="text-xs text-gray-500 mt-1">With a password, guests must enter it before watching.</p>
              </div>
            </div>
          )}

          {/* nav */}
          <div className="flex items-center justify-between gap-3 mt-6 pt-4 border-t border-gray-800">
            {step === 0 ? (
              isEditing ? (
                <button type="button" onClick={() => onDone?.()} className="text-sm text-gray-400 hover:text-gray-200">Cancel</button>
              ) : <span className="text-sm text-gray-700">Back</span>
            ) : (
              <button type="button" onClick={() => setStep(s => s - 1)} className="text-sm text-gray-400 hover:text-gray-200">Back</button>
            )}
            <div className="flex items-center gap-3">
              {step > 0 && !isLast && (
                <button type="button" onClick={() => setStep(s => s + 1)} className="text-sm text-gray-400 hover:text-gray-200">Skip</button>
              )}
              <Button
                onClick={next}
                disabled={(step === 0 && !subOk) || finish.isPending}
                className="px-6 py-2 text-sm font-semibold text-gray-900 rounded-xl hover:opacity-90 disabled:opacity-50"
                style={{ background: GOLD_GRAD }}>
                {finish.isPending ? "Saving…" : isLast ? "Finish & open studio" : "Next"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function LiveStreamPortal() {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [tab, setTab] = useState<PortalTab>(() => initialPortalTab(window.location.search));
  const [forceWizard, setForceWizard] = useState(false); // "Edit setup" re-run
  const qc = useQueryClient();

  const { data: streams = [], isLoading, error } = useQuery<LiveStream[]>({
    queryKey: ["/api/live-manage"],
    queryFn: () => apiRequest("/api/live-manage").then(r => r.json()),
  });

  const setup = useMutation({
    mutationFn: () =>
      apiRequest("/api/live-manage/setup", { method: "POST" }).then(async r => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          throw Object.assign(new Error(body?.error || "setup failed"), { status: r.status });
        }
        return r.json();
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/live-manage"] });
    },
  });

  // Live streaming is always enabled — auto-provision the stream on first visit
  // (no manual "activate" click). If the account has no wedding, setup errors and
  // we show the contact message instead.
  const noWedding = (setup.error as any)?.message?.includes("no wedding");
  useEffect(() => {
    if (!isLoading && !error && streams.length === 0 && !setup.isPending && !setup.error) {
      setup.mutate();
    }
  }, [isLoading, error, streams.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = streams.find(s => s.id === (selectedId ?? streams[0]?.id)) ?? null;
  const destinations = (selected as any)?.destinations ?? [];

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <Radio size={28} className="mx-auto mb-3 animate-spin" style={{ color: GOLD }} />
        <p className="text-gray-500">Loading your live studio…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center">
        <p className="text-red-400 text-sm">Could not load stream data. Please refresh.</p>
      </div>
    );
  }

  if (streams.length === 0) {
    // No wedding on the account → can't create a stream; show contact message.
    if (noWedding) {
      return (
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl border"
            style={{ borderColor: `${GOLD}66`, background: `${GOLD}11` }}>
            <Radio size={28} style={{ color: GOLD }} />
          </div>
          <h2 className="font-playfair text-2xl text-white mb-3">Live streaming</h2>
          <p className="text-sm text-amber-400 bg-amber-900/30 rounded-2xl px-4 py-3 max-w-sm mx-auto border border-amber-800">
            No wedding booking found for your account. Please contact LA Mirage if you believe this is an error.
          </p>
        </div>
      );
    }
    // Auto-provisioning (live streaming is enabled by default — no manual step).
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <Radio size={28} className="mx-auto mb-3 animate-spin" style={{ color: GOLD }} />
        <p className="text-gray-500">Preparing your live studio…</p>
        {setup.error && !noWedding && (
          <p className="text-sm text-red-400 mt-3">{(setup.error as any)?.message ?? "Something went wrong. Please refresh."}</p>
        )}
      </div>
    );
  }

  // First visit (mandatory) OR "Edit setup" re-run → the setup wizard.
  if (selected && (selected.setupComplete === false || forceWizard)) {
    return <SetupWizard stream={selected} onDone={forceWizard ? () => setForceWizard(false) : undefined} />;
  }

  return (
    <div className="max-w-4xl mx-auto px-3 sm:px-4 py-6 sm:py-8 space-y-6">
      {/* Stream selector (only shown when multiple) */}
      {streams.length > 1 && (
        <Card className="bg-gray-900 border-gray-700/60 rounded-2xl">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-gray-500 mb-2 font-medium uppercase tracking-wide">Select stream</p>
            <div className="flex gap-2 flex-wrap">
              {streams.map(s => (
                <button
                  key={s.id}
                  onClick={() => setSelectedId(s.id)}
                  className={`rounded-full px-3 py-1 text-sm border transition-colors ${
                    (selectedId ?? streams[0].id) === s.id
                      ? "text-gray-900 border-transparent font-medium"
                      : "bg-gray-800 text-gray-300 border-gray-700 hover:border-gray-500"
                  }`}
                  style={(selectedId ?? streams[0].id) === s.id ? { background: GOLD_GRAD } : undefined}
                >
                  {s.coupleNamesOverride ?? s.slug}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {selected && (
        <>
          {/* Cinematic hero */}
          <HeroHeader stream={selected} />

          {/* Edit setup — re-run the wizard */}
          <div className="flex justify-end -mt-2">
            <button
              type="button"
              onClick={() => setForceWizard(true)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-gray-200 transition-colors"
            >
              <Pencil size={12} /> Edit setup
            </button>
          </div>

          {/* Main tabs */}
          <Card className="bg-gray-900 border-gray-700/60 rounded-2xl">
            <CardContent className="pt-5 px-4 sm:px-6 pb-6">
              <Tabs value={tab} onValueChange={v => setTab(v as PortalTab)}>
                <div className="overflow-x-auto -mx-1 px-1 mb-6">
                  <TabsList className="bg-gray-800 border border-gray-700/60 rounded-xl h-auto p-1 w-max min-w-full sm:min-w-0 justify-start">
                    <TabsTrigger value="overview" className={TAB_TRIGGER_CLASS}>
                      <LayoutDashboard size={13} /> Overview
                    </TabsTrigger>
                    <TabsTrigger value="presentation" className={TAB_TRIGGER_CLASS}>
                      <Pencil size={13} /> Presentation
                    </TabsTrigger>
                    <TabsTrigger value="webaddress" className={TAB_TRIGGER_CLASS}>
                      <Globe size={13} /> Web address
                    </TabsTrigger>
                    <TabsTrigger value="social" className={TAB_TRIGGER_CLASS}>
                      <Youtube size={13} /> Social
                    </TabsTrigger>
                    <TabsTrigger value="wishes" className={TAB_TRIGGER_CLASS}>
                      <Heart size={13} /> Wishes
                    </TabsTrigger>
                    <TabsTrigger value="download" className={TAB_TRIGGER_CLASS}>
                      <Download size={13} /> Download
                    </TabsTrigger>
                    <TabsTrigger value="keepsake" className={TAB_TRIGGER_CLASS}>
                      <Gift size={13} /> Keepsake
                    </TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent value="overview">
                  <OverviewTab stream={selected} onNavigate={setTab} />
                </TabsContent>

                <TabsContent value="presentation">
                  <PresentationTab stream={selected} />
                </TabsContent>

                <TabsContent value="webaddress">
                  <WebAddressTab stream={selected} />
                </TabsContent>

                <TabsContent value="social">
                  <SocialKeysTab stream={selected} destinations={destinations} />
                </TabsContent>

                <TabsContent value="wishes">
                  <WishesTab stream={selected} />
                </TabsContent>

                <TabsContent value="download">
                  <DownloadTab stream={selected} />
                </TabsContent>

                <TabsContent value="keepsake">
                  <KeepsakeTab stream={selected} />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
